import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export interface PaginationControlsProps {
  /** Number of items currently displayed on this page */
  showing: number;
  /** Total number of items across all pages (if known) */
  totalCount?: number | undefined;
  /** Current page size */
  pageSize: number;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Current 1-based page number */
  currentPage: number;
  /** Total number of pages (computed from totalCount / pageSize) */
  totalPages?: number | undefined;
  /** Whether there is a previous page */
  hasPreviousPage: boolean;
  /** Whether there is a next page */
  hasNextPage: boolean;
  /** Navigate to previous page */
  onPreviousPage: () => void;
  /** Navigate to next page */
  onNextPage: () => void;
  /** Whether data is currently loading */
  isLoading?: boolean | undefined;
  /** Custom page size options (defaults to PAGE_SIZE_OPTIONS) */
  pageSizeOptions?: number[] | undefined;
}

export function PaginationControls({
  showing,
  totalCount,
  pageSize,
  onPageSizeChange,
  currentPage,
  totalPages,
  hasPreviousPage,
  hasNextPage,
  onPreviousPage,
  onNextPage,
  isLoading,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: PaginationControlsProps) {
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground px-1">
      {/* Left: count info */}
      <span>
        Showing {showing}
        {totalCount != null ? ` of ${totalCount}` : ''}{' '}
        item{(totalCount ?? showing) !== 1 ? 's' : ''}
      </span>

      {/* Center: page navigation */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Previous page"
          onClick={onPreviousPage}
          disabled={!hasPreviousPage || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-2 tabular-nums">
          Page {currentPage}
          {totalPages != null ? ` of ${totalPages}` : ''}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Next page"
          onClick={onNextPage}
          disabled={!hasNextPage || isLoading}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Right: page size selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground">
            {pageSize} per page
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {pageSizeOptions.map((size) => (
            <DropdownMenuItem
              key={size}
              onClick={() => { onPageSizeChange(size); }}
              className={cn(size === pageSize && 'font-medium')}
            >
              {size} per page
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
